import { useId, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@base-ui/react/dialog';
import type { CommsMember } from '@apis/comms';
import { CheckIcon, CloseIcon, MessageSquareIcon, PersonIcon } from '@utils/icons';
import { SearchInput } from '@components/ui/SearchInput/SearchInput';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { Avatar } from '@components/ui/Avatar/Avatar';
import { memberName } from '../utils/memberName';
import styles from './CreateConversationModal.module.css';

const STACK_LIMIT = 4; // Avatars shown in the footer summary; the count text covers the rest

function handleActivateKey(e: KeyboardEvent, activate: () => void) {
  if (e.key === ' ' || e.key === 'Enter') {
    e.preventDefault();
    activate();
  }
}

interface IMemberRowProps {
  member: CommsMember;
  role: 'checkbox' | 'radio';
  checked: boolean;
  onActivate: () => void;
}

/** One selectable row shared by the channel-members checkbox list and the DM radio list. */
function MemberRow({ member, role, checked, onActivate }: IMemberRowProps) {
  return (
    <div
      role={role}
      aria-checked={checked}
      tabIndex={0}
      className={checked ? `${styles.member} ${styles.selected}` : styles.member}
      onClick={onActivate}
      onKeyDown={(e) => handleActivateKey(e, onActivate)}
    >
      <Avatar name={memberName(member)} size={28} tinted />
      <span className={styles.memberName}>{memberName(member)}</span>
      {role === 'checkbox' && (
        <span className={styles.tick} aria-hidden="true">
          {checked && <CheckIcon size={10} />}
        </span>
      )}
    </div>
  );
}

/** What the parent receives on submit — a new channel or a DM to open. */
export type ConversationSubmit =
  | { kind: 'channel'; name: string; memberIds: string[] }
  | { kind: 'dm'; memberId: string };

interface ICreateConversationModalProps {
  open: boolean;
  members: CommsMember[];
  currentUserId: string;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (payload: ConversationSubmit) => void;
  initialValues?: { name: string; memberIds: string[] }; // If present, we're editing an existing channel
  /** Without it the channel tab is not rendered and the modal opens on direct messages (DMs need no channel right). */
  canCreateChannel: boolean;
}

export function CreateConversationModal({
  open,
  members,
  currentUserId,
  loading = false,
  onCancel,
  onSubmit,
  initialValues,
  canCreateChannel,
}: ICreateConversationModalProps) {
  const { t } = useTranslation();
  const nameId = useId();
  const isEditMode = initialValues !== undefined;
  const [tab, setTab] = useState<'channel' | 'dm'>(canCreateChannel ? 'channel' : 'dm');
  const [name, setName] = useState(initialValues?.name ?? '');
  // The current user is always a member (the backend force-includes the
  // creator on create/update), so they're never part of the toggleable set.
  const [memberIds, setMemberIds] = useState<string[]>(
    () => (initialValues?.memberIds ?? []).filter((id) => id !== currentUserId),
  );
  const [dmMemberId, setDmMemberId] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');

  function switchTab(next: 'channel' | 'dm') {
    setTab(next);
    setMemberQuery('');
    // Clear the other form's state when switching so stale input from a
    // previous visit to that tab can't be submitted accidentally.
    if (next === 'dm') {
      setName('');
      setMemberIds([]);
    } else {
      setDmMemberId(null);
    }
  }

  const showChannelForm = isEditMode || tab === 'channel'; // Edit mode is always the channel form; create mode honours the active tab.

  const currentMember = members.find((m) => m.id === currentUserId);

  const visibleMembers = useMemo(() => {
    const pool = members.filter((m) => m.id !== currentUserId);
    const normalizedQuery = memberQuery.trim().toLowerCase();
    if (!normalizedQuery) return pool;
    return pool.filter((m) => `${memberName(m)} ${m.username}`.toLowerCase().includes(normalizedQuery));
  }, [members, currentUserId, memberQuery]);

  const selectedMembers = useMemo(
    () => memberIds.map((id) => members.find((m) => m.id === id)).filter((m) => m !== undefined),
    [memberIds, members],
  );

  const dmMember = members.find((m) => m.id === dmMemberId);

  function toggleMember(id: string) {
    setMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function handleSubmit() {
    if (showChannelForm) {
      // Submit only ids that still resolve against the current roster — a
      // member who left the org between load and save has no row to remove
      // them from, so a stale id would otherwise ride along silently.
      onSubmit({ kind: 'channel', name: name.trim(), memberIds: selectedMembers.map((m) => m.id) });
    } else if (dmMemberId) {
      onSubmit({ kind: 'dm', memberId: dmMemberId });
    }
  }

  const canSubmit = !loading && (
    showChannelForm
      // Edit mode allows an empty selection — the creator alone remains.
      ? name.trim().length > 0 && (isEditMode || selectedMembers.length > 0)
      : dmMemberId !== null
  );

  const trimmedQuery = memberQuery.trim();

  const tabs: { id: 'channel' | 'dm'; icon: ReactNode; label: string }[] = [
    { id: 'channel', icon: '#', label: t('comms.createConversation.channelTab') },
    { id: 'dm', icon: <PersonIcon size={13} />, label: t('comms.createConversation.dmTab') },
  ];

  const search = (
    <div className={styles.fieldRow}>
      <SearchInput
        value={memberQuery}
        onChange={setMemberQuery}
        placeholder={t('comms.createConversation.searchMembersPlaceholder')}
        ariaLabelClear={t('comms.createConversation.clearSearch')}
      />
    </div>
  );

  const noResults = (
    <div className={styles.noResults}>
      {trimmedQuery
        ? t('comms.createConversation.noMemberResults', { query: trimmedQuery })
        : t('comms.createConversation.noOtherMembers')}
    </div>
  );

  let summary: ReactNode = null;
  if (showChannelForm && selectedMembers.length > 0) {
    summary = (
      <>
        <span aria-hidden="true">
          {selectedMembers.slice(0, STACK_LIMIT).map((member) => (
            <span key={member.id} className={styles.stackItem}>
              <Avatar name={memberName(member)} size={22} tinted />
            </span>
          ))}
        </span>
        <span className={styles.summaryText}>
          {t('comms.createConversation.memberCount', { count: selectedMembers.length + 1 })}
        </span>
      </>
    );
  } else if (!showChannelForm && dmMember) {
    summary = (
      <span className={styles.summaryText}>
        {t('comms.createConversation.dmSummary', { name: memberName(dmMember) })}
      </span>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup className={styles.panel}>
          <div className={styles.header}>
            <span className={styles.headerIcon}><MessageSquareIcon size={16} /></span>
            <Dialog.Title className={styles.title}>
              {isEditMode ? t('comms.editChannel.title') : t('comms.createConversation.title')}
            </Dialog.Title>
            <Dialog.Close className={styles.closeBtn} aria-label={t('common.close')}>
              <CloseIcon size={14} />
            </Dialog.Close>
          </div>

          {!isEditMode && canCreateChannel && (
            <div role="tablist" aria-label={t('comms.createConversation.title')} className={styles.tabs}>
              {tabs.map(({ id, icon, label }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={tab === id}
                  className={tab === id ? `${styles.tab} ${styles.selected}` : styles.tab}
                  onClick={() => switchTab(id)}
                >
                  <span className={styles.tabIcon} aria-hidden="true">{icon}</span>
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className={styles.body}>
            {showChannelForm ? (
              <>
                <div className={styles.section}>
                  <label className={styles.sectionTitle} htmlFor={nameId}>
                    {t('comms.createChannel.name')}
                  </label>
                  <div className={styles.fieldRow}>
                    <div className={styles.nameBox}>
                      <span aria-hidden="true">#</span>
                      <input
                        id={nameId}
                        className={styles.nameInput}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('comms.createChannel.namePlaceholder')}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>
                    <span>{t('comms.createChannel.members')}</span>
                    {selectedMembers.length > 0 && (
                      <span className={styles.sectionCount}>
                        {t('comms.createConversation.selectedCount', { count: selectedMembers.length })}
                      </span>
                    )}
                  </div>
                  {search}
                  <ScrollArea className={styles.members} viewportClassName={styles.membersBody}>
                    {currentMember && (
                      <div
                        role="checkbox"
                        aria-checked
                        aria-disabled="true"
                        className={`${styles.member} ${styles.memberFixed}`}
                      >
                        <Avatar name={memberName(currentMember)} size={28} tinted />
                        <span className={styles.memberName}>{memberName(currentMember)}</span>
                        <span className={styles.memberYou}>{t('comms.createConversation.you')}</span>
                      </div>
                    )}
                    {visibleMembers.length === 0 ? noResults : visibleMembers.map((member) => (
                      <MemberRow
                        key={member.id}
                        member={member}
                        role="checkbox"
                        checked={memberIds.includes(member.id)}
                        onActivate={() => toggleMember(member.id)}
                      />
                    ))}
                  </ScrollArea>
                </div>
              </>
            ) : (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span>{t('comms.createConversation.dmMembersLabel')}</span>
                </div>
                {search}
                <ScrollArea
                  className={styles.members}
                  viewportClassName={styles.membersBody}
                  viewportProps={{ role: 'radiogroup', 'aria-label': t('comms.createConversation.dmMembersLabel') }}
                >
                  {visibleMembers.length === 0 ? noResults : visibleMembers.map((member) => (
                    <MemberRow
                      key={member.id}
                      member={member}
                      role="radio"
                      checked={dmMemberId === member.id}
                      onActivate={() => setDmMemberId(member.id)}
                    />
                  ))}
                </ScrollArea>
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <span className={styles.summary}>{summary}</span>
            <button type="button" className={styles.cancelBtn} onClick={onCancel} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className={styles.submitBtn}
              onClick={handleSubmit}
              disabled={!canSubmit}
              aria-busy={loading}
            >
              {isEditMode
                ? t('common.save')
                : showChannelForm
                  ? t('comms.createChannel.title')
                  : t('comms.createConversation.dmConfirm')}
            </button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
